'use client';

import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { tv } from 'tailwind-variants';
import { useSwipeable } from 'react-swipeable';
import { faChevronRight, faChevronLeft, faX } from '@fortawesome/pro-solid-svg-icons';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from './icon.component';

import { GalleryImage } from '@/app/(main)/(pages)/_components/entities/gallery-carousel/gallery-carousel.component';

interface GalleryProps {
  images: GalleryImage[];
  open: boolean;
  currentIndex: number;
  onOpenChange: (open: boolean) => void;
}

const contentModal = tv({
  slots: {
    content: [
      'data-[state=open]:animate-content-show-mobile',
      'md:data-[state=open]:animate-content-show',
      'fixed',
      'md:rounded-2xl',
      'focus:outline-none z-[51]',
      'overflow-y-auto',
      'max-h-[50vh]',
      'top-[50%] bottom-auto left-[50%]',
      'right-auto translate-x-[-50%] translate-y-[-50%] w-full px-5 md:w-[70%] overflow-hidden',
    ],

    grid: 'flex items-center justify-between gap-10',
  },

  variants: {
    shouldShowNavigation: {
      false: {
        grid: 'justify-center',
      },
    },
  },
});

export function Gallery({ open, images, currentIndex, onOpenChange }: GalleryProps) {
  const { content, grid } = contentModal();
  const [currentIndexValue, setCurrentIndexValue] = useState(currentIndex);

  const shouldShowNavigation = images.length > 1;

  useEffect(() => {
    setCurrentIndexValue(currentIndex);
  }, [currentIndex]);

  function handleNextImage() {
    if (currentIndexValue < images.length - 1) {
      setCurrentIndexValue(currentIndexValue + 1);
    }
  }

  function handlePreviousImage() {
    if (currentIndexValue > 0) {
      setCurrentIndexValue(currentIndexValue - 1);
    }
  }

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      handleNextImage();
    },
    onSwipedRight: () => {
      handlePreviousImage();
    },
    trackMouse: true,
  });

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-black opacity-80 transition-opacity data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[50]">
          <Dialog.Close className="absolute top-10 right-10">
            <Icon icon={faX} className="text-white text-xl md:text-2xl" />
          </Dialog.Close>
        </Dialog.Overlay>
        <Dialog.Content className={content()}>
          <VisuallyHidden>
            <Dialog.Title>Galeria de imagens</Dialog.Title>
          </VisuallyHidden>
          <div className={grid({ shouldShowNavigation })}>
            {shouldShowNavigation && (
              <button onClick={handlePreviousImage}>
                <Icon icon={faChevronLeft} className="text-white text-3xl" />
              </button>
            )}

            {images.map((image, index) => (
              <Image
                key={image.src}
                src={image.src}
                width={320}
                height={320}
                alt="imagem expandida do parceiro"
                data-testid={DataTestIdEnum.GALLERY_IMAGE}
                className={
                  `rounded-lg max-h-[85vh] object-cover h-80 overflow-hidden  ${
                    currentIndexValue === index ? 'block' : 'hidden'}`
                }
                {...handlers}
              />
            ))}

            {shouldShowNavigation && (
              <button onClick={handleNextImage}>
                <Icon icon={faChevronRight} className="text-white text-3xl" />
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
